<script setup>
import BaseDeleteConfirm from '@/components/base/BaseDeleteConfirm.vue'
import BaseCustomerDisplay from '@/components/base/BaseCustomerDisplay.vue'
import ProductPriceFormDialog from './ProductPriceFormDialog.vue'

import { computed, defineProps, defineEmits } from 'vue'
import { formatCurrency, formatDate } from '@/helpers/stringHelpers'
import { calculateDayDifference, getCurrentDay } from '@/utils/DateHelpers'
import { useStore } from '@/utils/VuexHelpers'

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  width: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['refresh'])

const store = useStore()
const loadingProdPrice = computed(() => store.getters.loadingProdPrice)

const today = getCurrentDay()

const headerClass = 'blue-grey darken-1 white--text'
const pricingHeaders = computed(() => {
  return [
    { value: 'price_kg', text: 'Price/Kg', sortable: false, align: 'end' },
    {
      value: 'start_date',
      text: 'Effective Date',
      sortable: false,
      align: 'center'
    },
    { value: 'end_date', text: 'End Date', sortable: false, align: 'center' },
    { value: 'customer_id', text: 'Customer', sortable: false, align: 'start' },
    { value: 'comment', text: 'Comment', sortable: false },
    { value: 'actions', text: 'Actions', sortable: false, align: 'start' }
  ].map((header) => ({
    ...header,
    class: headerClass
  }))
})

const prices = computed(() => {
  const productPrices = Array.isArray(props.product?.prices)
    ? props.product.prices
    : []

  return productPrices.filter((price) => {
    return price.customer_id === null || price.customer_id === undefined
  })
})

const isFutureDate = (date) => {
  if (!date) return false

  return calculateDayDifference(today, date) > 0
}

const getStartDateClass = (date) => {
  return isFutureDate(date) ? 'yellow lighten-4 pa-1' : ''
}

const customSort = (items) => {
  return [...items].sort((a, b) => {
    const result = calculateDayDifference(a['start_date'], b['start_date'])

    return result
  })
}

const deletePrice = async (price) => {
  const payload = {
    id: price.id,
    product: props.product.product
  }

  const res = await store.dispatch('deleteProdPrice', { payload })

  if (res !== 'error') {
    emit('refresh')
  }
}
</script>

<template>
  <td :colspan="width" class="pa-4">
    <div class="d-flex justify-start mb-2">
      <ProductPriceFormDialog :product="product" @saved="emit('refresh')" />
    </div>

    <v-data-table
      :items="prices"
      :headers="pricingHeaders"
      :custom-sort="customSort"
      no-data-text="No pricing history available"
      dense
      hide-default-footer
      disable-pagination
    >
      <template v-slot:item.price_kg="{ value }">
        {{ formatCurrency(value) }}
      </template>

      <template v-slot:item.start_date="{ value }">
        <span :class="getStartDateClass(value)">{{ formatDate(value) }}</span>
      </template>

      <template v-slot:item.end_date="{ value }">
        {{ formatDate(value) }}
      </template>

      <template v-slot:item.customer_id="{ value }">
        <BaseCustomerDisplay :customer-id="value" display="code" />
      </template>

      <template v-slot:item.actions="{ item: price }">
        <v-row class="text-nowrap align-center flex-nowrap" no-gutters>
          <ProductPriceFormDialog
            :edit="true"
            :product="product"
            :price="price"
            @saved="emit('refresh')"
          />
          <BaseDeleteConfirm
            :icon="true"
            size="small"
            title="Are you sure you want to delete this product price?"
            @deleteConfirm="() => deletePrice(price)"
            :loading="loadingProdPrice === `delete-${price.id}`"
          >
            <template v-slot:content>
              <div class="text-subtitle-2 mt-2">Product Name:</div>
              <div class="font-weight-bold">
                {{ product.product }}
              </div>

              <div class="text-subtitle-2 mt-2">Price/Kg:</div>
              <div class="font-weight-bold">
                {{ formatCurrency(price.price_kg) }}
              </div>

              <div class="text-subtitle-2 mt-2">Effective Date:</div>
              <div class="font-weight-bold">
                {{ formatDate(price.start_date) }}
              </div>
            </template>
          </BaseDeleteConfirm>
        </v-row>
      </template>
    </v-data-table>
  </td>
</template>
